import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  useTheme,
  Chip,
} from "@mui/material";
import { format } from "date-fns";
import { createTheme } from "@mui/material/styles";
import { brand } from "../../shared-theme/themePrimitives";
import TablePagination from "@mui/material/TablePagination";

const ActivityFeed = ({ activities, page,
  rowsPerPage,
  totalRows,
  onPageChange,
  onRowsPerPageChange }) => {
  const theme = createTheme({
    palette: {
      primary: {
        light: "#4dabf5",
        main: "#1976d2",
        dark: "#1565c0",
        contrastText: "#fff",
        lighter: "#e3f2fd",
        darker: "#0d47a1",
      },
      secondary: {
        light: "#4ebaaa",
        main: "#00897b",
        dark: "#00695c",
        contrastText: "#fff",
        lighter: "#e0f2f1",
        darker: "#004d40",
      },
      error: {
        light: "#ef5350",
        main: "#d32f2f",
        dark: "#c62828",
        contrastText: "#fff",
      },
      warning: {
        light: "#ffb74d",
        main: "#ff9800",
        dark: "#f57c00",
        contrastText: "rgba(0, 0, 0, 0.87)",
      },
      info: {
        light: "#64b5f6",
        main: "#2196f3",
        dark: "#1976d2",
        contrastText: "#fff",
      },
      success: {
        light: "#81c784",
        main: "#4caf50",
        dark: "#388e3c",
        contrastText: "rgba(0, 0, 0, 0.87)",
      },
      grey: {
        50: "#fafafa",
        100: "#f5f5f5",
        200: "#eeeeee",
        300: "#e0e0e0",
        400: "#bdbdbd",
        500: "#9e9e9e",
        600: "#757575",
        700: "#616161",
        800: "#424242",
        900: "#212121",
      },
      background: {
        default: "#f5f7fa",
        paper: "#ffffff",
      },
      text: {
        primary: "rgba(0, 0, 0, 0.87)",
        secondary: "rgba(0, 0, 0, 0.6)",
        disabled: "rgba(0, 0, 0, 0.38)",
      },
    },
    typography: {
      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 600,
        fontSize: "2.5rem",
      },
      h2: {
        fontWeight: 600,
        fontSize: "2rem",
      },
      h3: {
        fontWeight: 600,
        fontSize: "1.75rem",
      },
      h4: {
        fontWeight: 600,
        fontSize: "1.5rem",
      },
      h5: {
        fontWeight: 600,
        fontSize: "1.25rem",
      },
      h6: {
        fontWeight: 600,
        fontSize: "1rem",
      },
      subtitle1: {
        fontSize: "1rem",
        fontWeight: 500,
      },
      subtitle2: {
        fontSize: "0.875rem",
        fontWeight: 500,
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.5,
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.5,
      },
      button: {
        textTransform: "none",
        fontWeight: 500,
      },
    },
    shape: {
      borderRadius: 8,
    },
    shadows: [
      "none",
      "0px 2px 1px -1px rgba(0,0,0,0.06),0px 1px 1px 0px rgba(0,0,0,0.04),0px 1px 3px 0px rgba(0,0,0,0.02)",
      "0px 3px 1px -2px rgba(0,0,0,0.06),0px 2px 2px 0px rgba(0,0,0,0.04),0px 1px 5px 0px rgba(0,0,0,0.02)",
      "0px 3px 3px -2px rgba(0,0,0,0.06),0px 3px 4px 0px rgba(0,0,0,0.04),0px 1px 8px 0px rgba(0,0,0,0.02)",
      "0px 2px 4px -1px rgba(0,0,0,0.06),0px 4px 5px 0px rgba(0,0,0,0.04),0px 1px 10px 0px rgba(0,0,0,0.02)",
      "0px 3px 5px -1px rgba(0,0,0,0.06),0px 5px 8px 0px rgba(0,0,0,0.04),0px 1px 14px 0px rgba(0,0,0,0.02)",
      "0px 3px 5px -1px rgba(0,0,0,0.06),0px 6px 10px 0px rgba(0,0,0,0.04),0px 1px 18px 0px rgba(0,0,0,0.02)",
      "0px 4px 5px -2px rgba(0,0,0,0.06),0px 7px 10px 1px rgba(0,0,0,0.04),0px 2px 16px 1px rgba(0,0,0,0.02)",
      "0px 5px 5px -3px rgba(0,0,0,0.06),0px 8px 10px 1px rgba(0,0,0,0.04),0px 3px 14px 2px rgba(0,0,0,0.02)",
      "0px 5px 6px -3px rgba(0,0,0,0.06),0px 9px 12px 1px rgba(0,0,0,0.04),0px 3px 16px 2px rgba(0,0,0,0.02)",
      "0px 6px 6px -3px rgba(0,0,0,0.06),0px 10px 14px 1px rgba(0,0,0,0.04),0px 4px 18px 3px rgba(0,0,0,0.02)",
      "0px 6px 7px -4px rgba(0,0,0,0.06),0px 11px 15px 1px rgba(0,0,0,0.04),0px 4px 20px 3px rgba(0,0,0,0.02)",
      "0px 7px 8px -4px rgba(0,0,0,0.06),0px 12px 17px 2px rgba(0,0,0,0.04),0px 5px 22px 4px rgba(0,0,0,0.02)",
      "0px 7px 8px -4px rgba(0,0,0,0.06),0px 13px 19px 2px rgba(0,0,0,0.04),0px 5px 24px 4px rgba(0,0,0,0.02)",
      "0px 7px 9px -4px rgba(0,0,0,0.06),0px 14px 21px 2px rgba(0,0,0,0.04),0px 5px 26px 4px rgba(0,0,0,0.02)",
      "0px 8px 9px -5px rgba(0,0,0,0.06),0px 15px 22px 2px rgba(0,0,0,0.04),0px 6px 28px 5px rgba(0,0,0,0.02)",
      "0px 8px 10px -5px rgba(0,0,0,0.06),0px 16px 24px 2px rgba(0,0,0,0.04),0px 6px 30px 5px rgba(0,0,0,0.02)",
      "0px 8px 11px -5px rgba(0,0,0,0.06),0px 17px 26px 2px rgba(0,0,0,0.04),0px 6px 32px 5px rgba(0,0,0,0.02)",
      "0px 9px 11px -5px rgba(0,0,0,0.06),0px 18px 28px 2px rgba(0,0,0,0.04),0px 7px 34px 6px rgba(0,0,0,0.02)",
      "0px 9px 12px -6px rgba(0,0,0,0.06),0px 19px 29px 2px rgba(0,0,0,0.04),0px 7px 36px 6px rgba(0,0,0,0.02)",
      "0px 10px 13px -6px rgba(0,0,0,0.06),0px 20px 31px 3px rgba(0,0,0,0.04),0px 8px 38px 7px rgba(0,0,0,0.02)",
      "0px 10px 13px -6px rgba(0,0,0,0.06),0px 21px 33px 3px rgba(0,0,0,0.04),0px 8px 40px 7px rgba(0,0,0,0.02)",
      "0px 10px 14px -6px rgba(0,0,0,0.06),0px 22px 35px 3px rgba(0,0,0,0.04),0px 8px 42px 7px rgba(0,0,0,0.02)",
      "0px 11px 14px -7px rgba(0,0,0,0.06),0px 23px 36px 3px rgba(0,0,0,0.04),0px 9px 44px 8px rgba(0,0,0,0.02)",
      "0px 11px 15px -7px rgba(0,0,0,0.06),0px 24px 38px 3px rgba(0,0,0,0.04),0px 9px 46px 8px rgba(0,0,0,0.02)",
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: "8px 16px",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
    },
  });

  // const activities = [
  // {
  //   id: 1,
  //   type: "ticket",
  //   title: "New ticket created",
  //   description: "Printer on 3rd floor is not working",
  //   timestamp: new Date(2023, 9, 5, 9, 41),
  //   user: {
  //     name: "John Smith",
  //   },
  // },
  // {
  //   id: 2,
  //   type: "incident",
  //   title: "Incident resolved",
  //   description: "Network outage in Building B",
  //   timestamp: new Date(2023, 9, 5, 8, 23),
  //   user: {
  //     name: "Maria Rodriguez",
  //   },
  // },
  // {
  //   id: 3,
  //   type: "change",
  //   title: "Change request approved",
  //   description: "Server upgrade scheduled for next week",
  //   timestamp: new Date(2023, 9, 4, 17, 12),
  //   user: {
  //     name: "Alex Johnson",
  //   },
  // },
  // {
  //   id: 4,
  //   type: "announcement",
  //   title: "System maintenance",
  //   description: "Scheduled downtime on Sunday, 10 PM - 2 AM",
  //   timestamp: new Date(2023, 9, 4, 15, 30),
  //   user: {
  //     name: "System Admin",
  //   },
  // },
  // {
  //   id: 5,
  //   type: "ticket",
  //   title: "Ticket closed",
  //   description: "Email access issue for user David Miller",
  //   timestamp: new Date(2023, 9, 4, 14, 15),
  //   user: {
  //     name: "Emma Wilson",
  //   },
  // },
  // ];

  const getTypeColor = (type) => {
    switch (type) {
      case "ticket":
        return theme.palette.info.main;
      case "incident":
        return theme.palette.error.main;
      case "change":
        return theme.palette.warning.main;
      case "announcement":
        return theme.palette.secondary.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getAvatarLetter = (name) => {
    return name.charAt(0).toUpperCase();
  };

  const getTimeString = (date) => {
    return format(date, "h:mm a");
  };

  const getDateString = (date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return format(date, "MMM d, yyyy");
    }
  };

  return (
    <Card
      sx={{
        height: "100%",
        transition: "all 0.3s ease",
        backgroundColor: theme.palette.mode === "light" ? "#fff" : brand[800],
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Recent Activity
        </Typography>
        <List
          sx={{
            width: "100%",
            p: 0,
          }}
        >
          {activities?.map((activity, index) => (
            <Box key={activity.id}>
              {index > 0 && <Divider variant="inset" component="li" />}
              <ListItem
                alignItems="flex-start"
                sx={{
                  px: 0,
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    alt={activity.performedBy}
                    src={activity.performedBy.avatar}
                    sx={{
                      bgcolor: getTypeColor(activity.type),
                    }}
                  >
                    {getAvatarLetter(activity.performedBy)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {activity.entityType}
                      </Typography>
                      <Chip
                        label={activity.actionType}
                        size="small"
                        sx={{
                          backgroundColor: `${getTypeColor(activity.actionType)}20`,
                          color: getTypeColor(activity.actionType),
                          fontWeight: 500,
                          fontSize: "0.7rem",
                          height: 20,
                        }}
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography
                        component="span"
                        variant="body2"
                        color="text.primary"
                      >
                        {activity.description}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 0.5,
                          color: theme.palette.text.secondary,
                          fontSize: "0.75rem",
                        }}
                      >
                        <span>{activity.performedBy}</span>
                        <span>
                          {new Date(activity.performedOn).toLocaleString()}
                          {/* {getDateString(activity.performedOn)} at{" "}
                          {getTimeString(activity.performedOn)} */}
                        </span>
                      </Box>
                    </>
                  }
                />
              </ListItem>
            </Box>
          ))}

          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[15, 50, 75, 100]}
            onPageChange={(event, newPage) => onPageChange(newPage)}
            onRowsPerPageChange={(event) => {
              onRowsPerPageChange(parseInt(event.target.value, 10));
            }}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              ".MuiTablePagination-toolbar": {
                minHeight: 42,
                paddingLeft: 1,
                paddingRight: 1
              },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                margin: 0
              }
            }}
          />
        </List>
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;
